import React from 'react';
import {
  BarChart2,
  Send,
  Eye,
  MousePointer,
  RefreshCw,
  FileText,
} from 'lucide-react';

const PRIMARY_ACTION_STYLE = {
  background: '#FFD700',
  color: '#000',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 14px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontWeight: 600,
};

const OUTLINE_ACTION_STYLE = {
  background: 'transparent',
  color: '#FFD700',
  border: '1px solid rgba(255,215,0,0.5)',
  borderRadius: '8px',
  padding: '8px 14px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

const PREVIEW_BOX_STYLE = {
  maxHeight: '360px',
  overflowY: 'auto',
  border: '1px solid rgba(255,215,0,0.25)',
  borderRadius: '10px',
  padding: '0.75rem',
  background: '#fff',
  color: '#111',
};

const EmailStatsModal = ({ stats, onClose, onReuse, onSaveAsTemplate }) => {
  if (!stats) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="stats-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stats-modal-header">
          <h3>
            <BarChart2 size={22} />
            Email Campaign Stats
          </h3>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="stats-subject">{stats.subject}</div>
        <div className="stats-date">Sent: {new Date(stats.sent_at).toLocaleString()}</div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
          <button
            type="button"
            onClick={(e) => onReuse({ id: stats.id, subject: stats.subject, message: stats.message }, e)}
            data-testid="modal-reuse-email-btn"
            style={PRIMARY_ACTION_STYLE}
          >
            <RefreshCw size={15} /> Reuse / Edit & Resend
          </button>
          <button
            type="button"
            onClick={(e) =>
              onSaveAsTemplate({ id: stats.id, subject: stats.subject, message: stats.message }, e)
            }
            data-testid="modal-save-template-btn"
            style={OUTLINE_ACTION_STYLE}
          >
            <FileText size={15} /> Save as Template
          </button>
        </div>

        {(stats.html_content || stats.message) && (
          <div className="stats-preview-section" style={{ marginTop: '1.25rem' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <FileText size={16} /> Email Preview
            </h4>
            <div data-testid="email-preview-content" style={PREVIEW_BOX_STYLE}>
              <iframe
                title={`email-preview-${stats.id}`}
                srcDoc={
                  stats.html_content ||
                  `<html><body style="font-family:sans-serif;padding:12px;">${stats.message}</body></html>`
                }
                sandbox=""
                style={{ width: '100%', height: '340px', border: 'none', background: '#fff' }}
              />
            </div>
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon sent"><Send size={20} /></div>
            <div className="stat-info">
              <span className="stat-number">{stats.successful}</span>
              <span className="stat-label">Delivered</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon opens"><Eye size={20} /></div>
            <div className="stat-info">
              <span className="stat-number">{stats.unique_opens}</span>
              <span className="stat-label">Opens ({stats.open_rate}%)</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon clicks"><MousePointer size={20} /></div>
            <div className="stat-info">
              <span className="stat-number">{stats.unique_clicks}</span>
              <span className="stat-label">Clicks ({stats.click_rate}%)</span>
            </div>
          </div>
        </div>

        {stats.opened_by?.length > 0 && (
          <div className="stats-detail-section">
            <h4><Eye size={16} /> Opened by ({stats.opened_by.length})</h4>
            <div className="stats-email-list">
              {stats.opened_by.map((email) => (
                <span key={email} className="stats-email-tag">{email}</span>
              ))}
            </div>
          </div>
        )}

        {stats.clicked_by?.length > 0 && (
          <div className="stats-detail-section">
            <h4><MousePointer size={16} /> Clicked by ({stats.clicked_by.length})</h4>
            <div className="stats-email-list">
              {stats.clicked_by.map((email) => (
                <span key={email} className="stats-email-tag clicked">{email}</span>
              ))}
            </div>
          </div>
        )}

        <p className="stats-note">
          Note: Tracking data updates when recipients open emails or click links.
          Some email clients may block tracking pixels.
        </p>
      </div>
    </div>
  );
};

export default EmailStatsModal;
